/**
 * Notification Service
 *
 * Client-side service for notification operations and URL generation
 */
import { db, functions } from '@/js/firebase';
import {
  doc,
  updateDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { logger } from './loggerService';

/**
 * Notification type definitions with display info and categories
 */
export const NOTIFICATION_TYPES = {
  deliverable_assigned: {
    label: 'Deliverable Assigned',
    description: 'When a deliverable is assigned to you',
    category: 'deliverables'
  },
  deliverable_unassigned: {
    label: 'Deliverable Unassigned',
    description: 'When a deliverable is reassigned away from you',
    category: 'deliverables'
  },
  deliverable_status_changed: {
    label: 'Deliverable Status Changed',
    description: 'When a deliverable status changes',
    category: 'deliverables'
  },
  review_ready: {
    label: 'Review Ready',
    description: 'When a deliverable is ready for your review',
    category: 'deliverables',
    clientOnly: true
  },
  project_in_review: {
    label: 'New Project Submitted',
    description: 'When a client submits a new project for review',
    category: 'projects',
    adminOnly: true
  },
  agency_review_needed: {
    label: 'Request Needs Your Review',
    description: 'When one of your companies submits a request for your approval',
    category: 'projects',
    agencyOnly: true
  },
  client_project_edited: {
    label: 'Client Edited Project',
    description: 'When a client requests changes to an existing project',
    category: 'projects',
    adminOnly: true
  },
  project_canceled: {
    label: 'Project Canceled',
    description: 'When a client cancels a project',
    category: 'projects',
    adminOnly: true
  },
  comment_mention: {
    label: 'Mentioned in Comment',
    description: 'When you are @mentioned in a comment',
    category: 'deliverables'
  },
  comment_new: {
    label: 'New Comment',
    description: 'When someone comments on your deliverable',
    category: 'deliverables'
  },
  calendar_event_added: {
    label: 'Added to Event',
    description: 'When you are added to a calendar event',
    category: 'calendar'
  },
  calendar_event_removed: {
    label: 'Removed from Event',
    description: 'When you are removed from a calendar event',
    category: 'calendar'
  },
  calendar_reminder_week: {
    label: '7-Day Reminder',
    description: 'Reminder 7 days before an event',
    category: 'calendar'
  },
  calendar_reminder_day: {
    label: '24-Hour Reminder',
    description: 'Reminder 24 hours before an event',
    category: 'calendar'
  },
  client_date_selection_needed: {
    label: 'Date Selection Needed',
    description: 'When you need to select a date for an event',
    category: 'calendar',
    clientOnly: true
  },
  client_date_selected: {
    label: 'Client Selected Date',
    description: 'When a client selects a date for an event',
    category: 'calendar',
    adminOnly: true
  },
  client_date_unscheduled: {
    label: 'Client Unscheduled Date',
    description: 'When a client unschedules an event date',
    category: 'calendar',
    adminOnly: true
  },
  assets_uploaded: {
    label: 'Assets Uploaded',
    description: 'When new assets are uploaded to your company',
    category: 'assets',
    clientOnly: true
  },
  video_encode_failed: {
    label: 'Video Failed to Encode',
    description: 'When a video you uploaded fails to encode even after an automatic retry',
    category: 'assets'
  },
  daily_check_in: {
    label: 'Daily Check In',
    description: 'Daily summary of submitted projects and DGF totals',
    category: 'projects',
    adminOnly: true
  },
  msa_invoice_deposit: {
    label: 'MSA Deposit Invoice',
    description: 'Email reminders to invoice the 50% deposit when an MSA order is submitted',
    category: 'billing',
    adminOnly: true,
    defaultOff: true
  },
  msa_invoice_final: {
    label: 'MSA Final Invoice',
    description: 'Email reminders to invoice the final 50% once an MSA project is complete',
    category: 'billing',
    adminOnly: true,
    defaultOff: true
  },
  podcast_publish_scheduling: {
    label: 'Podcast Publish Scheduling',
    description:
      'Reminders to schedule approved episodes on Spotify, Apple, and YouTube, and alerts when a client moves a publish date',
    category: 'shows',
    adminOnly: true,
    defaultOff: true
  },
  email_delivery_failures: {
    label: 'Email Delivery Failures',
    description: "When a user's emails fail to deliver repeatedly (3+ in 30 days)",
    category: 'companies',
    adminOnly: true,
    defaultOff: true
  },
  delivery_risk_digest: {
    label: 'Delivery Risk Digest',
    description: 'Daily 8am summary of deliverables that are late, idle, or slipping',
    category: 'deliverables',
    adminOnly: true
  },
  episode_publish_date_needed: {
    label: 'Publish Date Needed',
    description: 'When an approved episode is still waiting on a publish date',
    category: 'shows',
    clientOnly: true
  },
  storage_warning: {
    label: 'Storage Warning',
    description: 'When a company reaches 80% storage capacity',
    category: 'storage',
    adminOnly: true
  },
  storage_critical: {
    label: 'Storage Critical',
    description: 'When a company reaches 95% storage capacity',
    category: 'storage',
    adminOnly: true
  },
  storage_overage: {
    label: 'Storage Overage',
    description: 'When a company exceeds their storage limit',
    category: 'storage',
    adminOnly: true
  },
  storage_grace_reminder: {
    label: 'Grace Period Reminder',
    description: 'Reminder about storage grace period',
    category: 'storage',
    adminOnly: true
  },
  storage_grace_expired: {
    label: 'Grace Period Expired',
    description: 'When a storage grace period has expired',
    category: 'storage',
    adminOnly: true
  },
  storage_billing_required: {
    label: 'Billing Required',
    description: 'When your account needs billing information added to avoid suspension',
    category: 'storage'
  },
  storage_contract_started: {
    label: 'New Paid Subscription',
    description: 'When a company starts paying for a storage plan',
    category: 'storage',
    adminOnly: true
  },
  storage_contract_upgraded: {
    label: 'Storage Upgraded',
    description: 'When a company upgrades their storage tier',
    category: 'storage',
    adminOnly: true
  },
  storage_contract_downgraded: {
    label: 'Storage Downgraded',
    description: 'When a company downgrades their storage tier',
    category: 'storage',
    adminOnly: true
  },
  storage_contract_canceled: {
    label: 'Storage Canceled',
    description: 'When a company cancels their storage subscription',
    category: 'storage',
    adminOnly: true
  },
  time_off_submitted: {
    label: 'Time Off Submitted',
    description: 'When someone submits a time-off request for your approval',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_approved: {
    label: 'Time Off Approved',
    description: 'When your time-off request is approved',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_rejected: {
    label: 'Time Off Rejected',
    description: 'When your time-off request is rejected',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_cancelled: {
    label: 'Time Off Canceled',
    description: 'When a time-off request is cancelled',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_sick_logged: {
    label: 'Sick Day Logged',
    description: 'When someone logs a sick day',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_modified: {
    label: 'Time Off Modified',
    description: 'When approved time-off dates are modified',
    category: 'timeoff',
    adminOnly: true
  },
  time_off_sick_extended: {
    label: 'Sick Day Extended',
    description: 'When someone extends their sick day return date',
    category: 'timeoff',
    adminOnly: true
  },
  show_blueprint_ready: {
    label: 'Blueprint Ready',
    description: 'When a show blueprint is ready for your review',
    category: 'shows',
    clientOnly: true
  },
  show_blueprint_revisions: {
    label: 'Blueprint Revisions Requested',
    description: 'When a client requests revisions on a blueprint',
    category: 'shows',
    adminOnly: true
  },
  show_blueprint_approved: {
    label: 'Blueprint Approved',
    description: 'When a client approves a show blueprint',
    category: 'shows',
    adminOnly: true
  },
  conversation_new: {
    label: 'New Conversation',
    description: 'When a client starts a conversation you respond to',
    category: 'conversations',
    adminOnly: true
  },
  conversation_reply: {
    label: 'Conversation Reply',
    description: 'When someone replies in a conversation you are part of',
    category: 'conversations'
  },
  conversation_assigned: {
    label: 'Conversation Assigned',
    description: 'When a conversation is assigned to you',
    category: 'conversations',
    adminOnly: true
  },
  conversation_quiet: {
    label: 'Conversation Gone Quiet',
    description: "When a client hasn't replied after both reminders",
    category: 'conversations',
    adminOnly: true
  },
  conversation_email_failed: {
    label: 'Conversation Email Failed',
    description: "When an email to a client in a conversation you're handling bounces or is dropped",
    category: 'conversations',
    adminOnly: true
  },
  conversation_sla_breach: {
    label: 'Conversation Response Overdue',
    description: 'When a conversation passes its response-time threshold',
    category: 'conversations',
    adminOnly: true
  },
  company_created: {
    label: 'New Company Added',
    description: 'When a new company is added to Vysual',
    category: 'companies',
    adminOnly: true
  },
  feedback_submitted: {
    label: 'New Feedback Submitted',
    description: 'When someone files a bug report or feature request',
    category: 'feedback',
    adminOnly: true,
    superOnly: true,
    defaultOff: true
  },
  error_logged: {
    label: 'New Error Logged',
    description: 'When a new production error lands in the error log (at most one alert per 15 minutes)',
    category: 'feedback',
    adminOnly: true,
    superOnly: true,
    defaultOff: true
  },
  feedback_completed: {
    label: 'Your Feedback Was Completed',
    description: 'When a bug you reported is fixed or a feature you requested ships',
    category: 'feedback',
    adminOnly: true
  }
};

/**
 * Notification category definitions
 */
export const NOTIFICATION_CATEGORIES = {
  deliverables: {
    label: 'Deliverables',
    icon: 'file-lines',
    color: 'is-info'
  },
  projects: {
    label: 'Projects',
    icon: 'folder',
    color: 'is-success'
  },
  calendar: {
    label: 'Calendar Events',
    icon: 'calendar',
    color: 'is-primary'
  },
  assets: {
    label: 'Assets',
    icon: 'images',
    color: 'is-success'
  },
  storage: {
    label: 'Storage',
    icon: 'database',
    color: 'is-warning'
  },
  timeoff: {
    label: 'Time Off',
    icon: 'umbrella-beach',
    color: 'is-info'
  },
  shows: {
    label: 'Shows',
    icon: 'podcast',
    color: 'is-link'
  },
  conversations: {
    label: 'Conversations',
    icon: 'message-lines',
    color: 'is-primary'
  },
  companies: {
    label: 'Companies',
    icon: 'building',
    color: 'is-link'
  },
  billing: {
    label: 'Billing',
    icon: 'file-invoice-dollar',
    color: 'is-warning'
  },
  feedback: {
    label: 'Feedback',
    icon: 'bug',
    color: 'is-link'
  }
};

/**
 * Default notification preferences structure
 * Channel-specific type preferences allow granular control
 */
const DEFAULT_NOTIFICATION_PREFERENCES = {
  email: {
    enabled: true,
    frequency: 'instant', // 'instant' | 'digest' | 'none'
    types: {
      deliverable_assigned: true,
      deliverable_unassigned: true,
      deliverable_status_changed: true,
      review_ready: true,
      project_in_review: true,
      agency_review_needed: true,
      client_project_edited: true,
      comment_mention: true,
      comment_new: true,
      calendar_event_added: true,
      calendar_event_removed: true,
      calendar_reminder_week: true,
      calendar_reminder_day: true,
      client_date_selection_needed: true,
      client_date_selected: true,
      client_date_unscheduled: true,
      assets_uploaded: true,
      daily_check_in: true,
      storage_warning: true,
      storage_critical: true,
      storage_overage: true,
      storage_grace_reminder: true,
      storage_grace_expired: true,
      storage_contract_started: true,
      storage_contract_upgraded: true,
      storage_billing_required: true,
      storage_contract_downgraded: true,
      storage_contract_canceled: true
    }
  },
  googleChat: {
    enabled: false,
    spaceId: null,        // Google Chat DM space ID
    googleEmail: null,    // Google Workspace email if different from Vysual email
    linkedAt: null,
    types: {
      deliverable_assigned: true,
      deliverable_unassigned: true,
      deliverable_status_changed: true,
      review_ready: true,
      project_in_review: true,
      agency_review_needed: true,
      client_project_edited: true,
      comment_mention: true,
      comment_new: true,
      calendar_event_added: true,
      calendar_event_removed: true,
      calendar_reminder_week: true,
      calendar_reminder_day: true,
      client_date_selection_needed: true,
      client_date_selected: true,
      client_date_unscheduled: true,
      assets_uploaded: true,
      daily_check_in: true,
      storage_warning: true,
      storage_critical: true,
      storage_overage: true,
      storage_grace_reminder: true,
      storage_grace_expired: true,
      storage_contract_started: true,
      storage_contract_upgraded: true,
      storage_billing_required: true,
      storage_contract_downgraded: true,
      storage_contract_canceled: true
    }
  },
  browser: {
    enabled: true,  // In-app notifications enabled by default
    types: {
      deliverable_assigned: true,
      deliverable_unassigned: true,
      deliverable_status_changed: true,
      review_ready: true,
      project_in_review: true,
      agency_review_needed: true,
      client_project_edited: true,
      comment_mention: true,
      comment_new: true,
      calendar_event_added: true,
      calendar_event_removed: true,
      calendar_reminder_week: true,
      calendar_reminder_day: true,
      client_date_selection_needed: true,
      client_date_selected: true,
      client_date_unscheduled: true,
      assets_uploaded: true,
      daily_check_in: true,
      storage_warning: true,
      storage_critical: true,
      storage_overage: true,
      storage_grace_reminder: true,
      storage_grace_expired: true,
      storage_contract_started: true,
      storage_contract_upgraded: true,
      storage_billing_required: true,
      storage_contract_downgraded: true,
      storage_contract_canceled: true
    }
  },
  // Legacy types field for backwards compatibility
  types: {
    deliverable_assigned: true,
    deliverable_unassigned: true,
    deliverable_status_changed: true,
    project_in_review: true,
    agency_review_needed: true,
    client_project_edited: true,
    comment_mention: true,
    comment_new: true,
    calendar_event_added: true,
    calendar_event_removed: true,
    calendar_reminder_week: true,
    calendar_reminder_day: true,
    client_date_selection_needed: true,
    client_date_selected: true,
    client_date_unscheduled: true,
    assets_uploaded: true,
    daily_check_in: true,
    storage_warning: true,
    storage_critical: true,
    storage_overage: true,
    storage_grace_reminder: true,
    storage_grace_expired: true,
    storage_contract_upgraded: true,
    storage_billing_required: true,
    storage_contract_downgraded: true,
    storage_contract_canceled: true
  }
};

class NotificationService {
  constructor() {
    this.logger = logger;
  }

  /**
   * Mark a notification as read
   * @param {String} userId - User ID
   * @param {String} notificationId - Notification ID
   */
  async markAsRead(userId, notificationId) {
    if (!userId || !notificationId) {
      this.logger.warn('[NotificationService] markAsRead called with missing params', { userId, notificationId });
      return;
    }

    try {
      const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
      await updateDoc(notifRef, {
        isRead: true,
        readAt: serverTimestamp()
      });

      this.logger.info('[NotificationService] Notification marked as read', { notificationId });
    } catch (error) {
      this.logger.error('[NotificationService] Failed to mark as read:', error);
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   * @param {String} userId - User ID
   * @param {Array<String>} notificationIds - Array of notification IDs
   */
  async markMultipleAsRead(userId, notificationIds) {
    if (!userId || !notificationIds?.length) {
      this.logger.warn('[NotificationService] markMultipleAsRead called with missing params');
      return;
    }

    try {
      const batch = writeBatch(db);
      const now = serverTimestamp();

      notificationIds.forEach(notificationId => {
        const notifRef = doc(db, 'users', userId, 'notifications', notificationId);
        batch.update(notifRef, {
          isRead: true,
          readAt: now
        });
      });

      await batch.commit();

      this.logger.info('[NotificationService] Multiple notifications marked as read', {
        count: notificationIds.length
      });
    } catch (error) {
      this.logger.error('[NotificationService] Failed to mark multiple as read:', error);
      throw error;
    }
  }

  /**
   * Get the navigation URL for a notification based on user type
   * Returns null for deliverables (which open in a modal instead of navigating)
   * @param {Object} notification - The notification object
   * @param {String} userType - The user type ('admin', 'super', or 'user')
   * @param {String|null} agencyId - The recipient's agencyId, for agency deep links
   * @returns {String|null} The URL to navigate to, or null for modal-based entities
   */
  getNotificationUrl(notification, userType, agencyId = null) {
    const { entityType, entityId, companyId } = notification;

    if (!entityType || !entityId) {
      this.logger.warn('[NotificationService] Notification missing entityType or entityId', notification);
      return '/';
    }

    // Deliverables open in a modal, not via URL navigation
    if (entityType === 'deliverable') {
      return null;
    }

    const isAdmin = userType === 'admin' || userType === 'super';

    // Episode deep links carry "showId:projectId" in entityId — the /reviews hub
    // excludes primary-episode deliverables, so they need their own page.
    const [epShowId, epProjectId] = String(entityId).split(':');
    const episodeUrl = (tab) =>
      companyId && epShowId && epProjectId
        ? `/accounts/${companyId}/shows/${epShowId}/episodes/${epProjectId}?tab=${tab}`
        : `/accounts/${companyId}/reviews`;

    // Build URL based on entity type and user type
    const routes = {
      project: isAdmin
        ? `/nexus/projects/edit/${entityId}`
        : `/accounts/${companyId}/projects/${entityId}`,
      calendar_event: `/nexus/calendar/${entityId}`, // Calendar events only for admin/super users
      // Upload is a company-page section now (/nexus/companies/details/:id/upload).
      // The old `/nexus/asset-upload?company=` never matched a route at all —
      // before the reshuffle the path was /nexus/assets/upload.
      assets: isAdmin
        ? `/nexus/companies/details/${companyId}/upload`
        : `/accounts/${companyId}/assets`,
      review: `/accounts/${companyId}/reviews`,
      episode_review: episodeUrl('review'),
      episode_publish: episodeUrl('publish'),
      show: isAdmin
        ? `/nexus/podcasts/${entityId}`
        : `/accounts/${companyId}/shows/${entityId}`,
      conversation: isAdmin
        ? `/nexus/conversations?c=${entityId}`
        : `/accounts/${companyId}/conversations/${entityId}`,
      company: isAdmin
        ? `/nexus/companies/details/${entityId}`
        : null,
      // Storage contract notifications (billing required, grace periods):
      // the company page, whose Storage tab carries the action.
      subscription: isAdmin
        ? `/nexus/companies/details/${companyId}`
        : `/accounts/${companyId}/company`,
      // The agency's own queue, opened on that request. The project itself is
      // in a company portal, but the ACTION lives here.
      //
      // The agency comes from the RECIPIENT, not the notification: createNotification
      // does not persist `metadata` onto the doc, and the notification already lives
      // in this user's own subcollection, so they are the agency staffer by definition.
      agency_review: agencyId
        ? `/agency/${agencyId}/requests?review=${entityId}`
        : null,
      msa_invoicing: isAdmin ? '/nexus/msa-invoicing' : null,
      podcast_publish: isAdmin ? '/nexus/podcast-publishing' : null,
      delivery_risk: isAdmin ? '/nexus/delivery-risk' : null,
      email_delivery: isAdmin ? '/nexus/email-delivery' : null,
      feedback: isAdmin ? '/nexus/feedback' : null
    };

    const url = routes[entityType];

    if (!url) {
      this.logger.warn('[NotificationService] Unknown entityType', { entityType });
      return '/';
    }

    // Append query params if present (e.g., ?tab=discussion)
    if (notification.queryParams) {
      return `${url}?${notification.queryParams}`;
    }

    return url;
  }

  /**
   * Check if notification should open a modal instead of navigating
   * @param {Object} notification - The notification object
   * @returns {Boolean} True if notification should open a modal
   */
  shouldOpenModal(notification) {
    return notification.entityType === 'deliverable';
  }

  /**
   * Get icon for notification type
   * @param {String} type - Notification type
   * @returns {Array} FontAwesome icon array [prefix, iconName]
   */
  getNotificationIcon(type) {
    const icons = {
      deliverable_assigned: ['fas', 'user-check'],
      deliverable_unassigned: ['fas', 'user-minus'],
      deliverable_status_changed: ['fas', 'circle-check'],
      review_ready: ['fas', 'magnifying-glass'],
      project_in_review: ['fas', 'flag-checkered'],
      agency_review_needed: ['fas', 'inbox'],
      client_project_edited: ['fas', 'pen-to-square'],
      project_canceled: ['fas', 'ban'],
      msa_invoice_deposit: ['fas', 'file-invoice-dollar'],
      msa_invoice_final: ['fas', 'file-invoice-dollar'],
      podcast_publish_scheduling: ['fas', 'calendar-check'],
      delivery_risk_digest: ['fas', 'triangle-exclamation'],
      email_delivery_failures: ['fas', 'envelope-circle-check'],
      comment_mention: ['fas', 'at'],
      comment_new: ['fas', 'comment'],
      calendar_event_added: ['fas', 'calendar-plus'],
      calendar_event_removed: ['fas', 'calendar-xmark'],
      calendar_reminder_week: ['fas', 'calendar-week'],
      calendar_reminder_day: ['fas', 'calendar-day'],
      client_date_selection_needed: ['fas', 'calendar-exclamation'],
      client_date_selected: ['fas', 'calendar-check'],
      client_date_unscheduled: ['fas', 'calendar-xmark'],
      assets_uploaded: ['fas', 'images'],
      daily_check_in: ['fas', 'clipboard-list'],
      storage_warning: ['fas', 'exclamation-triangle'],
      storage_critical: ['fas', 'exclamation-circle'],
      storage_overage: ['fas', 'database'],
      storage_grace_reminder: ['fas', 'clock'],
      storage_grace_expired: ['fas', 'ban'],
      storage_contract_upgraded: ['fas', 'arrow-up'],
      storage_billing_required: ['fas', 'credit-card'],
      storage_contract_downgraded: ['fas', 'arrow-down'],
      storage_contract_canceled: ['fas', 'times-circle'],
      show_blueprint_ready: ['fas', 'file-lines'],
      show_blueprint_revisions: ['fas', 'pen-to-square'],
      show_blueprint_approved: ['fas', 'circle-check'],
      conversation_new: ['fas', 'message-lines'],
      conversation_reply: ['fas', 'message-lines'],
      conversation_assigned: ['fas', 'user-check'],
      conversation_sla_breach: ['fas', 'clock'],
      conversation_email_failed: ['fas', 'envelope-circle-check'],
      conversation_quiet: ['fas', 'hourglass-end'],
      company_created: ['fas', 'building'],
      feedback_submitted: ['fas', 'bug'],
      error_logged: ['fas', 'triangle-exclamation'],
      feedback_completed: ['fas', 'circle-check'],
      video_encode_failed: ['fas', 'triangle-exclamation']
    };

    return icons[type] || ['fas', 'bell'];
  }

  /**
   * Get color class for notification type
   * @param {String} type - Notification type
   * @returns {String} Bulma color class
   */
  getNotificationColor(type) {
    const colors = {
      deliverable_assigned: 'has-text-success',
      deliverable_unassigned: 'has-text-warning',
      deliverable_status_changed: 'has-text-info',
      review_ready: 'has-text-link',
      project_in_review: 'has-text-primary',
      agency_review_needed: 'has-text-warning',
      client_project_edited: 'has-text-warning',
      project_canceled: 'has-text-danger',
      msa_invoice_deposit: 'has-text-danger',
      msa_invoice_final: 'has-text-danger',
      podcast_publish_scheduling: 'has-text-info',
      delivery_risk_digest: 'has-text-danger',
      email_delivery_failures: 'has-text-danger',
      error_logged: 'has-text-danger',
      comment_mention: 'has-text-link',
      comment_new: 'has-text-info',
      calendar_event_added: 'has-text-success',
      calendar_event_removed: 'has-text-danger',
      calendar_reminder_week: 'has-text-warning',
      calendar_reminder_day: 'has-text-danger',
      client_date_selection_needed: 'has-text-warning',
      client_date_selected: 'has-text-success',
      client_date_unscheduled: 'has-text-warning',
      assets_uploaded: 'has-text-success',
      daily_check_in: 'has-text-primary',
      storage_warning: 'has-text-warning',
      storage_critical: 'has-text-danger',
      storage_overage: 'has-text-danger',
      storage_grace_reminder: 'has-text-warning',
      storage_grace_expired: 'has-text-danger',
      storage_contract_upgraded: 'has-text-success',
      storage_billing_required: 'has-text-warning',
      storage_contract_downgraded: 'has-text-warning',
      storage_contract_canceled: 'has-text-danger',
      show_blueprint_ready: 'has-text-link',
      show_blueprint_revisions: 'has-text-warning',
      show_blueprint_approved: 'has-text-success',
      conversation_new: 'has-text-primary',
      conversation_reply: 'has-text-primary',
      conversation_assigned: 'has-text-primary',
      conversation_sla_breach: 'has-text-warning',
      conversation_email_failed: 'has-text-danger',
      conversation_quiet: 'has-text-warning',
      company_created: 'has-text-link'
    };

    return colors[type] || 'has-text-grey';
  }

  /**
   * Format notification timestamp for display
   * @param {Object} timestamp - Firestore timestamp or Date
   * @returns {String} Formatted time string
   */
  formatTimestamp(timestamp) {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    // Less than 1 minute
    if (diff < 60000) {
      return 'Just now';
    }

    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    }

    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }

    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }

    // Otherwise show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Get default notification preferences
   * @returns {Object} Default preferences object
   */
  getDefaultPreferences() {
    return { ...DEFAULT_NOTIFICATION_PREFERENCES };
  }

  // ==========================================================================
  // Google Chat Integration (Chat API)
  // ==========================================================================

  /**
   * Generate a linking code for Google Chat connection
   * Used when user's Google email differs from their Vysual email
   * @returns {Promise<{code: string, expiresAt: string}>}
   */
  async generateGoogleChatLinkCode() {
    try {
      const generateCodeFn = httpsCallable(functions, 'generateGoogleChatLinkCode');
      const result = await generateCodeFn();

      this.logger.info('[NotificationService] Google Chat link code generated');
      return result.data;
    } catch (error) {
      this.logger.error('[NotificationService] Failed to generate link code:', error);

      const errorMessage = error.message || 'Failed to generate linking code';
      throw new Error(errorMessage);
    }
  }

  /**
   * Unlink Google Chat from user's notifications
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async unlinkGoogleChat() {
    try {
      const unlinkGoogleChatFn = httpsCallable(functions, 'unlinkGoogleChat');
      const result = await unlinkGoogleChatFn();

      this.logger.info('[NotificationService] Google Chat unlinked successfully');
      return result.data;
    } catch (error) {
      this.logger.error('[NotificationService] Failed to unlink Google Chat:', error);
      throw new Error('Failed to disconnect Google Chat');
    }
  }

  /**
   * Test Google Chat connection
   * Sends a test notification via the Chat API
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async testGoogleChat() {
    try {
      const testGoogleChatFn = httpsCallable(functions, 'testGoogleChat');
      const result = await testGoogleChatFn();

      this.logger.info('[NotificationService] Google Chat test successful');
      return result.data;
    } catch (error) {
      this.logger.error('[NotificationService] Google Chat test failed:', error);
      throw new Error('Failed to send test notification');
    }
  }
}

export const notificationService = new NotificationService();
